// OutlineView — collapsible tree (the default OrgChart view).
//
// Scales well to 1000+ people because only opened branches are in the
// DOM. Each row is a clickable line; hover surfaces the "Edit" action.
//
// Lifted verbatim from the pre-multi-view OrgChart so existing UX is
// preserved as the default. Other view files import shared.js helpers
// so role colours, name resolution, and search semantics stay
// consistent across all five renderings.
import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  ROLE_COLOR, colorFor, childrenOf, nameOf, nodeMatches,
} from "./shared";

function Avatar({ name, role }) {
  const color = colorFor(role);
  return (
    <div className="w-8 h-8 rounded-xl border flex items-center justify-center text-xs font-bold shrink-0"
      style={{ borderColor: `${color}40`, background: `${color}12`, color }}>
      {String(name || "?")[0].toUpperCase()}
    </div>
  );
}

function OrgNode({ node, depth = 0, search, onSelectUser }) {
  // Depth-2 default keeps the typical org visible without overwhelming
  // the page; deeper levels open on click.
  const [expanded, setExpanded] = useState(depth < 2);
  const children = childrenOf(node);
  const hasChildren = children.length > 0;

  if (search && !nodeMatches(node, search)) return null;

  const matchesSearch = search && nodeMatches(node, search);
  const color = colorFor(node.role);

  return (
    <div className={`${depth > 0 ? "ml-6 pl-4 border-l border-white/5" : ""}`}>
      <div
        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 group cursor-pointer transition-colors hover:bg-white/3 ${matchesSearch ? "bg-[#00ff90]/3" : ""}`}
        onClick={() => { if (hasChildren) setExpanded((v) => !v); }}
      >
        <span className="w-4 shrink-0 text-white/20">
          {hasChildren
            ? (expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />)
            : <span className="block w-px h-4 mx-auto bg-white/10" />}
        </span>

        <Avatar name={nameOf(node)} role={node.role} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white/80 font-medium truncate">{nameOf(node)}</span>
            <span className="text-xs capitalize px-1.5 py-0.5 rounded-full border"
              style={{ color, borderColor: `${color}30`, background: `${color}10` }}>
              {node.role || "staff"}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-white/30 truncate">{node.title || node.designation || ""}</span>
            {node.department && <span className="text-xs text-white/20">· {node.department}</span>}
          </div>
        </div>

        {hasChildren && (
          <span className="text-xs text-white/25 shrink-0">
            {children.length} report{children.length !== 1 ? "s" : ""}
          </span>
        )}

        <button onClick={(e) => { e.stopPropagation(); onSelectUser?.(node); }}
          className="text-xs px-2 py-1 rounded-lg border border-[#00ff90]/15 bg-[#00ff90]/5 text-[#00ff90]/50 hover:text-[#00ff90] hover:border-[#00ff90]/30 opacity-0 group-hover:opacity-100 transition-all shrink-0">
          Edit
        </button>
      </div>

      {expanded && hasChildren && (
        <div className="mt-0.5">
          {children.map((child, i) => (
            <OrgNode key={child.id || i} node={child} depth={depth + 1} search={search} onSelectUser={onSelectUser} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function OutlineView({ tree, search, onSelectUser }) {
  return (
    <div className="p-4 max-h-[600px] overflow-y-auto">
      {tree.map((node, i) => (
        <OrgNode key={node.id || i} node={node} depth={0} search={search} onSelectUser={onSelectUser} />
      ))}
      {/* Role legend lives here so it's adjacent to the rows that use the colours. */}
      <div className="mt-4 pt-3 border-t border-white/5 flex gap-4 flex-wrap">
        {Object.entries(ROLE_COLOR).map(([role, color]) => (
          <span key={role} className="flex items-center gap-1.5 text-xs text-white/30 capitalize">
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />{role}
          </span>
        ))}
      </div>
    </div>
  );
}
